import React, { CSSProperties, useState, useEffect, useRef } from "react";
import { Header, Image, Segment } from "semantic-ui-react";
import IconTab from "./IconTab";
import ProfileSection from "./ProfileSection";
import FormSection from "./FormSection";
import BookingSection from "./BookingSection";
import NoteSection from "./NoteSection";

// Serializers
import PatientQueueSerializer from "issara-sdk/types/PatientQueueSerializer_apps_PRX";
import PatientSerializer from "issara-sdk/types/PatientSerializer_apps_REG";
import PatientAppointmentSerializer from "issara-sdk/types/PatientAppointmentSerializer_apps_QUE";
import DiagFormSerializer from "issara-sdk/types/DiagFormSerializer_apps_PRX";
import ProxyPatientSerializer from "issara-sdk/types/ProxyPatientSerializer_apps_PRX";

type ProfilePanelProps = {
  onEvent: (e: any) => any;
  selectedChat?: PatientQueueSerializer;
  selectedProxyPatient?: ProxyPatientSerializer;
  patientData?: PatientSerializer;
  activeTabs: TabType[];
  forms?: DiagFormSerializer[];
  appointments?: PatientAppointmentSerializer[];
  // todo --- ยังไม่มี Serializer Note
  notes?: Array<{
    id: string;
    date: string;
    content: string;
    author: string;
    createdAt: string;
  }>;
  onSaveNote?: (content: string) => void;
  onUpdateNote?: (id: string, content: string) => void;
  onDeleteNote?: (id: string) => void;
  onToggleTab: (tab: TabType) => void;
};

type TabType = "profile" | "form" | "booking" | "note";

const styles = {
  mainSegment: {
    width: "325px",
    borderLeft: "1px solid #CCCCCC",
    borderTop: "none",
    borderRight: "none",
    borderBottom: "none",
    borderRadius: 0,
    margin: 0,
    height: "100%",
    overflowY: "auto",
    padding: "20px 0",
  } as CSSProperties,
  center: { textAlign: "center" } as CSSProperties,
  profileImage: { width: "62px", height: "62px", marginBottom: "1rem" } as CSSProperties,
  noMargin: { margin: 0 } as CSSProperties,
  iconTabsContainer: {
    display: "flex",
    justifyContent: "space-around",
    marginTop: "20px",
    marginBottom: "20px",
    padding: "0 2rem",
  } as CSSProperties,
};

const ProfilePanel: React.FC<ProfilePanelProps> = (props) => {
  // const [activeTabs, setActiveTabs] = useState<TabType[]>([]);
  const [collapsedSections, setCollapsedSections] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    const patient = props.selectedChat?.patient;
    const division = props.selectedChat?.division;

    if (patient) {
      props.onEvent({ message: "GetPatientChatDetail", params: { patient } });
      props.onEvent({ message: "GetPatientAppointmentList", params: { patient, division } });
      props.onEvent({ message: "GetPatientDiagFormList", params: { patient, division } });
      props.onEvent({ message: "GetProxyPatient", params: { patient } });
    }
  }, [props.selectedChat?.patient, props.selectedChat?.division]);

  // const toggleTab = (tab: TabType) => {
  //   setActiveTabs((prev) => {
  //     return prev.includes(tab) ? prev.filter((t) => t !== tab) : [...prev, tab];
  //   });
  // };

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <Segment style={styles.mainSegment}>
      {/* Profile Section */}
      <div style={styles.center}>
        <Image
          src={props.selectedChat?.image || "/static/images/person.png"}
          circular
          centered
          style={styles.profileImage}
        />
        <Header as="h4" style={styles.noMargin}>
          {props.patientData?.first_name} {props.patientData?.last_name}
        </Header>
      </div>

      {/* Icon Tabs */}
      <div style={styles.iconTabsContainer}>
        {/* Profile Icon */}
        <IconTab
          iconName="user"
          label="Profile"
          // isActive={activeTabs.includes("profile")}
          // onClick={() => toggleTab("profile")}
          isActive={props.activeTabs.includes("profile")}
          onClick={() => props.onToggleTab("profile")}
        />

        {/* Form Icon */}
        <IconTab
          iconName="file alternate"
          label="Form"
          // isActive={activeTabs.includes("form")}
          // onClick={() => toggleTab("form")}
          isActive={props.activeTabs.includes("form")}
          onClick={() => props.onToggleTab("form")}
          badgeCount={props.forms?.length || 0}
        />

        {/* Booking Icon */}
        <IconTab
          iconName="calendar"
          label="Booking"
          // isActive={activeTabs.includes("booking")}
          // onClick={() => toggleTab("booking")}
          isActive={props.activeTabs.includes("booking")}
          onClick={() => props.onToggleTab("booking")}
          badgeCount={props.appointments?.length || 0}
        />

        {/* Note Icon */}
        <IconTab
          iconName="sticky note"
          label="Note"
          // isActive={activeTabs.includes("note")}
          // onClick={() => toggleTab("note")}
          isActive={props.activeTabs.includes("note")}
          onClick={() => props.onToggleTab("note")}
          badgeCount={props.notes?.length || 0}
        />
      </div>

      {/* Content Sections */}
      <div style={{ marginTop: "20px" }}>
        {props.activeTabs.map((tab, index) => {
          if (tab === "profile") {
            return (
              <ProfileSection
                key={`profile-${index}`}
                patientData={props.patientData}
                proxyPatient={props.selectedProxyPatient}
                isCollapsed={collapsedSections.personalInfo || false}
                onToggle={() => toggleSection("personalInfo")}
              />
            );
          }

          if (tab === "form") {
            return (
              <FormSection
                key={`form-${index}`}
                forms={props.forms}
                isCollapsed={collapsedSections.forms || false}
                onToggle={() => toggleSection("forms")}
              />
            );
          }

          if (tab === "booking") {
            return (
              <BookingSection
                key={`booking-${index}`}
                appointments={props.appointments}
                isCollapsed={collapsedSections.bookings || false}
                onToggle={() => toggleSection("bookings")}
              />
            );
          }

          if (tab === "note") {
            return (
              <NoteSection
                key={`note-${index}`}
                notes={props.notes}
                isCollapsed={collapsedSections.notes || false}
                onToggle={() => toggleSection("notes")}
                onSaveNote={props.onSaveNote}
                onUpdateNote={props.onUpdateNote}
                onDeleteNote={props.onDeleteNote}
              />
            );
          }

          return null;
        })}
      </div>
    </Segment>
  );
};

export default ProfilePanel;
